import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { IOutcome } from '@frontend/oxygen/shared-utils';
import * as _ from 'underscore';

import { EnvironmentConfig } from '../../../client-config/index';
import { IRacingEvent } from '../../../core/models/racing-event.model';
import { IRacingMarket } from '../../../core/models/racing-market.model';
import { CmsService } from '../../../core/services/cms/cms.service';
import { ISystemConfig } from '../../../core/services/cms/models';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { LocaleService } from '../../../core/services/locale/locale.service';
import { NativeBridgeService } from '../../../core/services/nativeBridge/native-bridge.service';
import { RacingPostApiService } from '../../../core/services/racing/racingPost/racing-post-api.service';
import { IRaceResultRunnersData, IRacingPostGHResponse, IRacingPostHRResponse } from '../../../core/services/racing/racingPost/racing-post.model';
import { RacingResultsService } from '../../../core/services/sport/racing-results.service';
import { TimeService } from '../../../core/services/time/time.service';
import { UserService } from '../../../core/services/user/user.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { racing } from '../../../lazy-modules/locale/translations/en-US/racing.lang';
import { ILazyComponentOutput } from '../../../shared/components/lazy-component/lazy-component.model';
import { IStreamControl } from '../../../tote/models/stream-control.model';

@Component({
    templateUrl: 'racing-event-resulted.component.html',
    styleUrls: ['./racing-event-resulted.component.scss'],
    selector: 'racing-event-resulted',
    standalone: false,
})
export class RacingEventResultedComponent implements OnInit, OnDestroy {
    @Input() eventEntity: IRacingEvent;
    @Input() filter: string;
    @Input() isGreyhoundEdp: boolean;
    @Input() streamControl?: IStreamControl;
    @Input() preEventMarkets: IRacingMarket[];
    unPlaced: boolean = false;
    totalResults: boolean;
    eventDateSufx: string;
    antepostTerms: string;
    isCashout: boolean;
    resultsResponseError: string;
    isGreyhoundsFullResultsEnabled: boolean;
    greyhoundsFullResultsData: IRacingPostGHResponse;
    preloadStream: boolean;
    isDesktop: boolean;
    isCoral: boolean;
    OneApiCheck = true;
    oneApiResultRunners: IRaceResultRunnersData[];

    private readonly tagName: string = 'RacingEventResulted';
    isEditStable: boolean = false;
    resultsResponse: boolean = false;
    stableState: string;
    horseSvg: string;
    nativeVideoPlayerPlaceholderRef: ElementRef;
    private readonly environment = inject(EnvironmentConfig).configuration;
    constructor(
        protected timeService: TimeService,
        private cd: ChangeDetectorRef,
        private racingResultsService: RacingResultsService,
        private racingPostApiService: RacingPostApiService,
        private locale: LocaleService,
        private pubsubService: PubSubService,
        private gtmService: GtmService,
        private cmsService: CmsService,
        protected nativeBridgeService: NativeBridgeService,
        protected windowRef: WindowRefService,
        protected deviceService: DeviceService,
        protected userService: UserService,
    ) {}

    ngOnInit(): void {
        this.isCoral = this.environment && this.environment.BRAND === 'bma';

        this.pubsubService.subscribe(this.tagName, this.pubsubService.API.RELOAD_RACING_EVENT_RESULTED, () => {
            this.ngOnDestroy();
            this.ngOnInit();
        });
        this.filter = 'hideStream';

        this.cmsService.getSystemConfig().subscribe((config: ISystemConfig) => {
            this.isGreyhoundsFullResultsEnabled = !!config?.GreyhoundFullResults?.enabled;

            if (this.eventEntity.isUKorIRE && this.eventEntity.categoryCode === 'GREYHOUNDS' && this.isGreyhoundsFullResultsEnabled) {
                this.racingPostApiService
                    .getGreyhoundRaceOneApiResultDetails(this.eventEntity.id.toString())
                    .subscribe((eventResultData: IRacingPostGHResponse) => {
                        this.greyhoundsFullResultsData = eventResultData;
                    });
            }

            this.racingResultsService.getRacingResults(this.eventEntity, this.isFullResultsRequired()).then(
                (event) => {
                    this.resultsResponse = false;
                    this.eventEntity = event;
                    const wewMarket = this.eventEntity.resultedWEWMarket;
                    if (this.eventEntity.categoryId === racing.categoryId && this.eventEntity.isUKorIRE) {
                        this.getUnplacedRaceResults();
                    }
                    if (!(wewMarket && wewMarket.outcomes && wewMarket.outcomes.length && wewMarket.hasPositions)) {
                        this.checkRacingResults();
                    } else {
                        this.antepostTerms = wewMarket.isEachWayAvailable ? this.formatAntepostTerms(wewMarket.terms) : '';
                        this.isCashout = wewMarket.cashoutAvail === 'Y' || wewMarket.viewType === 'handicaps';
                        this.extendOutcome(wewMarket!.outcomes!);
                        this.extendOutcome(wewMarket!.unPlaced!);
                        if (wewMarket.nonRunners && wewMarket.nonRunners.length) {
                            this.extendOutcome(wewMarket.nonRunners);
                        }
                        this.cd.detectChanges();
                    }
                },
                () => {
                    this.resultsResponseError = this.locale.getString('racing.noRacingResultsFound');
                },
            );
        });

        this.eventDateSufx = this.getEventDate();
        this.isDesktop = this.deviceService.isDesktop;
        this.cd.detectChanges();
    }
    checkRacingResults() {
        this.eventEntity.resultedWEWMarket = { ...this.eventEntity!.sortedMarkets![0]! };
        this.racingResultsService.getRacingPositions(this.eventEntity).subscribe((res) => {
            const responseResults = res[0] && res[0].racingResult.children;
            this.extendMarketOutcome(responseResults);
            this.racingResultsService.mapRulesDividendsToOutcomes(res, this.eventEntity.resultedWEWMarket, true);
            this.racingResultsService.sortOutcomesByPositions(this.eventEntity.resultedWEWMarket);
            this.racingResultsService.filterDividends(this.eventEntity.resultedWEWMarket);
            this.racingResultsService.filterRulesDeduction(this.eventEntity.resultedWEWMarket);
            this.resultsResponse = true;
            this.eventEntity.voidResult = false;
            this.eventEntity.resultedWEWMarket.hasPositions = true;
            this.extendWEWMarket(this.eventEntity.resultedWEWMarket);
            this.cd.detectChanges();
        });
    }
    extendWEWMarket(wewMarket: any) {
        this.antepostTerms = wewMarket.isEachWayAvailable ? this.formatAntepostTerms(wewMarket.terms) : '';
        this.isCashout = wewMarket.cashoutAvail === 'Y' || wewMarket.viewType === 'handicaps';
        this.extendOutcome(wewMarket.outcomes);
        this.extendOutcome(wewMarket.unPlaced);
        if (wewMarket.nonRunners && wewMarket.nonRunners.length) {
            this.extendOutcome(wewMarket.nonRunners);
        }
    }
    extendMarketOutcome(racingResults: any) {
        // @ts-ignore
        this!.eventEntity!.resultedWEWMarket!.outcomes! = this!
            .eventEntity!.resultedWEWMarket!.outcomes!.map((outcome) => {
                const responseOutcome = racingResults && racingResults.find((routcome: any) => routcome?.finalPosition?.outcomeId === outcome.id);
                if (responseOutcome?.finalPosition?.runnerNumber) {
                    outcome.results = {};
                    outcome = _.extend(outcome, responseOutcome['finalPosition']);
                    outcome.results.position = outcome.position;
                    outcome.results.priceNum = responseOutcome['finalPosition'].startingPriceNum;
                    outcome.results.priceDen = responseOutcome['finalPosition'].startingPriceDen;
                    outcome.results.priceDec = responseOutcome['finalPosition'].startingPriceDec;
                    return outcome!;
                }
                return null;
            })
            .filter(Boolean)!;
    }
    /**
     * get lose and non runners horses data
     */
    getUnplacedRaceResults() {
        this.racingPostApiService
            .getHorseRaceOneApiResultDetails(this.eventEntity.id.toString())
            .subscribe((eventResultData: IRacingPostHRResponse) => {
                if (_.isEmpty(eventResultData)) {
                    return;
                }
                const resultedEventData = eventResultData.document[this.eventEntity.id.toString()];
                const resultsDataCheck = resultedEventData.hasOwnProperty('results');
                const runnersDataCheck = resultsDataCheck && resultedEventData!.results!.hasOwnProperty('runners');
                if (resultsDataCheck && runnersDataCheck) {
                    this.oneApiResultRunners = resultedEventData?.results?.runners ?? [];
                }
                const totalHorses = resultedEventData.horses;
                const winHorses = this.eventEntity.resultedWEWMarket.outcomes;
                const loseHorses = this.eventEntity.resultedWEWMarket.unPlaced;
                const nonRunners = this.eventEntity.resultedWEWMarket.nonRunners;
                const nonRunnersResulted =
                    resultedEventData.results && resultedEventData.results.nonRunners
                        ? resultedEventData.results.nonRunners.split(',').length
                        : nonRunners!.length;
                const contestHorses = totalHorses.length - nonRunnersResulted;

                if (resultsDataCheck && runnersDataCheck && loseHorses && contestHorses === resultedEventData!.results!.runners!.length) {
                    this.OneApiCheck = false;
                    this.cd.detectChanges();
                    this.racingResultsService.mapResultPositionPlaced(winHorses, resultedEventData!.results!.runners!);
                    this.racingResultsService.mapResultPositionUnplaced(loseHorses, resultedEventData!.results!.runners!, this.eventEntity);
                    this.totalResults = false;
                    this.OneApiCheck = true;
                    this.cd.detectChanges();
                } else {
                    this.totalResults = true;
                    return console.log('racing.incompleteResultsData');
                }
            });
    }

    ngOnDestroy(): void {
        this.pubsubService.unsubscribe(this.tagName);
        this.pubsubService.unsubscribe('RacingEventComponent');
    }

    getEventDate(): string {
        return this.timeService.getFullDateFormatSufx(new Date(this.eventEntity.startTime));
    }

    formatAntepostTerms(str: string): string {
        const newStr = str
            .replace(/(odds)/gi, 'Odds')
            .replace(/(places)/gi, 'Places')
            .replace(/\d+\/\d+( odds)/gi, (match) => {
                return `${match}`;
            });
        return newStr.replace(/[0-9]+(?!.*[0-9])/, (match) => `${match}`);
    }

    private extendOutcome(outcomes: IOutcome[]): void {
        _.each(outcomes, (outcome: IOutcome) => {
            const racingForm = _.findWhere(this.eventEntity!.sortedMarkets![0]!.outcomes!, { runnerNumber: outcome.runnerNumber });
            if (racingForm) {
                outcome.racingFormOutcome = racingForm.racingFormOutcome;
            }
        });
    }

    /**
     * Check For horse racing and greyhounds full results required or not
     * @returns void
     */
    private isFullResultsRequired(): boolean {
        return (
            this.eventEntity.isUKorIRE &&
            (this.eventEntity.categoryCode === 'HORSE_RACING' ||
                (this.eventEntity.categoryCode === 'GREYHOUNDS' && this.isGreyhoundsFullResultsEnabled))
        );
    }

    /**
     * set GA tracking object
     * @param gtmEventLabel string value
     */
    setGtmData(gaEventDetails: string): void {
        const gtmData = {
            'event': 'Event.Tracking',
            'component.CategoryEvent': 'horse racing',
            'component.LabelEvent': 'event details page',
            'component.ActionEvent': 'click',
            'component.PositionEvent': 'not applicable',
            'component.LocationEvent': this.eventEntity.typeName,
            'component.EventDetails': gaEventDetails,
            'component.URLclicked': 'not applicable',
        };
        this.gtmService.push(gtmData.event, gtmData);
    }
    playStream(e: MouseEvent): void {
        e.preventDefault();
        this.setGtmData('watch replay');
        this.filter = this.filter === 'showVideoStream' ? 'hideStream' : 'showVideoStream';
        this.preloadStream = true;
        this.pubsubService.subscribe('RacingEventComponent', this.pubsubService.API.VIDEO_STREAM_ERROR_DIALOG_CLOSED, () => {
            this.filter = 'hideStream';
        });
    }

    saveOrEditStable(event: any) {
        this.isEditStable = event.value;
    }
    onVideoStreamEventClose(output: ILazyComponentOutput) {
        if (!this.isDesktop && output.output === 'playStreamError') {
            this.filter = 'hideStream';
        }
    }
}
